import api from '@/utils/axios';
import { unwrapItem, unwrapList } from '@/utils/apiHelpers';

export interface ClientDashboardStats {
  total_orders_this_month: number;
  active_deliveries: number;
  total_spent: number;
  monthly_savings: number;
}

export interface ClientOrderSummary {
  id: number;
  code: string;
  status: string;
  package_description: string;
  delivery_type?: string | null;
  price: number;
  created_at: string;
  departure_zone?: string | null;
  arrival_zone?: string | null;
  driver_name?: string | null;
  driver_type?: 'community' | 'professional' | null;
}

export interface CommunityTrip {
  id: number;
  driver_id: number;
  driver_name: string;
  status: string;
  from: { city: string };
  to: { city: string };
  departure_date: string;
  available_space: number;
  price_per_kg: number;
  rating: number;
  deliveries_count: number;
}

export interface ClientDashboardData {
  stats: ClientDashboardStats;
  pending_orders: ClientOrderSummary[];
  active_orders: ClientOrderSummary[];
  recent_history: ClientOrderSummary[];
  community_trips: CommunityTrip[];
}

export interface WalletTransaction {
  id: string;
  type: 'order_payment' | 'withdrawal' | 'deposit' | 'refund' | 'cod_collection';
  amount: number;
  status: string;
  provider?: string | null;
  reference?: string | null;
  created_at: string;
}

export interface ClientWalletData {
  balance: number;
  pending_amount: number;
  monthly_savings: number;
  pending_withdrawals: WalletTransaction[];
  transactions: WalletTransaction[];
}

export async function fetchClientDashboard(): Promise<ClientDashboardData> {
  const res = await api.get('/client/dashboard');
  return unwrapItem<ClientDashboardData>(res.data);
}

export async function fetchClientWallet(): Promise<ClientWalletData> {
  const res = await api.get('/client/wallet');
  return unwrapItem<ClientWalletData>(res.data);
}

export async function fetchCommunityTrips(): Promise<CommunityTrip[]> {
  const res = await api.get('/client/community-trips');
  return unwrapList<CommunityTrip>(res.data);
}

export interface WalletProvider {
  code: string;
  name: string;
}

export async function fetchWalletProviders(): Promise<WalletProvider[]> {
  const res = await api.get('/client/wallet/providers');
  return unwrapList<WalletProvider>(res.data);
}

export interface WalletOperationPayload {
  amount: number;
  payer_phone: string;
  mobile_money_provider?: 'orange' | 'mtn';
  provider?: string;
}

export async function depositClientWallet(payload: WalletOperationPayload): Promise<ClientWalletData> {
  const res = await api.post('/client/wallet/deposit', payload);
  return unwrapItem<ClientWalletData>(res.data);
}

export async function withdrawClientWallet(payload: WalletOperationPayload): Promise<ClientWalletData> {
  const res = await api.post('/client/wallet/withdraw', payload);
  return unwrapItem<ClientWalletData>(res.data);
}

export interface ClientNotification {
  id: string;
  titre: string;
  message: string;
  est_lu: boolean;
  created_at: string;
  type: 'order' | 'delivery' | 'payment' | string;
  reference_id?: number;
}

export async function fetchClientNotifications(): Promise<ClientNotification[]> {
  const res = await api.get('/client/notifications');
  return unwrapList<ClientNotification>(res.data);
}

export async function markClientNotificationRead(notificationKey: string): Promise<void> {
  await api.post(`/client/notifications/${encodeURIComponent(notificationKey)}/read`);
}

export async function markAllClientNotificationsRead(): Promise<void> {
  await api.post('/client/notifications/read-all');
}
